import { useSyncExternalStore } from 'react'
import { ApiError, durationToMs, timeLabel } from '@bloo-team/api-client'
import type { DqlTime } from '@bloo-team/api-client'

/**
 * The Cases area's time range (docs/cases.md §Time range) — one value for
 * the session, shared by the listing and every case page: the statistics,
 * the attach-signals search, and (once the service filters them) the case
 * list and the timeline. In memory only: not in the URL, gone on reload.
 * Its shape is the console's time picker value (TimeRangePicker): a
 * relative duration, or an absolute local from–to.
 */

/** The quick ranges the Cases picker offers. */
export const CASES_RANGE_PRESETS = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
] as const

export const DEFAULT_CASES_RANGE: DqlTime = { kind: 'relative', duration: '7d' }

/** case-metrics takes at most 366 days. */
export const CASES_RANGE_MAX_MS = 366 * 86_400_000

/**
 * Refuses bounds over CASES_RANGE_MAX_MS before any read, as the service
 * would (422) — the one check the listing, the statistics and the closure
 * trend share, so all three say the same thing.
 */
export function checkCasesRange(b: { from: number; to: number }): void {
  if (b.to - b.from > CASES_RANGE_MAX_MS) throw new ApiError(422, 'Pick a range of at most 366 days.', null)
}

let current: DqlTime = DEFAULT_CASES_RANGE
const listeners = new Set<() => void>()

export function setCasesRange(next: DqlTime) {
  current = next
  for (const l of listeners) l()
}

/** Test hook. */
export function resetCasesRange() {
  setCasesRange(DEFAULT_CASES_RANGE)
}

export function useCasesRange(): [DqlTime, (next: DqlTime) => void] {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => current,
    () => current,
  )
  return [value, setCasesRange]
}

/** Epoch-ms bounds of a range; a relative one ends now. Absolute times are local. */
export function rangeBounds(r: DqlTime, now = Date.now()): { from: number; to: number } {
  if (r.kind === 'relative') return { from: now - (durationToMs(r.duration) ?? 7 * 86_400_000), to: now }
  return { from: new Date(r.start).getTime(), to: new Date(r.end).getTime() }
}

/** `7d`, or `10-06 09:00 → 10-13 18:00` — short, for headings and notes. */
export const rangeLabel = (r: DqlTime) => (r.kind === 'relative' ? r.duration : timeLabel(r))

/** A stable query-key part: presets by name; absolute by their bounds. */
export const rangeKey = (r: DqlTime) => (r.kind === 'relative' ? r.duration : `${r.start}/${r.end}`)
